// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../utils/gql.ts'

const query = /* GraphQL */ gqlAst`
  query COMANDAS_COCINA_VERSION($codigoSucursal: Int!, $areas: [String!]!) {
    restCocinaVersion(codigoSucursal: $codigoSucursal, areas: $areas) {
      area
      version
    }
  }
`

export const KEY_COMANDAS_COCINA_VERSION = 'comandas-cocina-version'

/** Valor de version cuando Redis no está disponible: recargar la lista directamente */
export const COCINA_VERSION_NO_DISPONIBLE = -1

export interface ApiComandasCocinaVersionParams {
  codigoSucursal: number
  /** _id de las áreas (Area); impresora si el artículo no tiene áreas; o GENERAL. [] = todas las áreas */
  areas: string[]
}

export interface ComandaCocinaVersionProps {
  /** _id del área consultada */
  area: string
  /** Versión del área; -1 si Redis no está disponible */
  version: number
}

/**
 * Versión de cocina por área. Solo lee Redis (no consulta Mongo):
 * la pantalla de cocina la consulta cada pocos segundos y pide restCocinaComandas solo cuando cambia.
 * version -1 (COCINA_VERSION_NO_DISPONIBLE): Redis no disponible, recargar la lista directamente.
 * @param params Sucursal y áreas a consultar.
 * @param options Headers adicionales y señal de cancelación de la petición.
 */
export const apiComandasCocinaVersion = async (
  params: ApiComandasCocinaVersionParams,
  options: GraphqlClientOptionsProps = {},
): Promise<ComandaCocinaVersionProps[]> => {
  try {
    const data = await graphqlClient.request<{ restCocinaVersion: ComandaCocinaVersionProps[] }>({
      document: query,
      variables: { ...params },
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.restCocinaVersion
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida las versiones de cocina desde cualquier lugar, sin necesidad de tener montado el hook.
 * @example invalidateComandasCocinaVersion()
 * @example invalidateComandasCocinaVersion({ extraQueryKeys: [codigoSucursal] })
 */
export const invalidateComandasCocinaVersion = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_COMANDAS_COCINA_VERSION, options)

/**
 * Hook para consultar la versión de cocina de una o varias áreas.
 * queryKey: [KEY_COMANDAS_COCINA_VERSION, codigoSucursal, areas, ...(options.queryKeys ?? [])]
 * Pensado para polling: pasar `refetchInterval` en options y, si alguna versión cambia
 * (o es -1), refrescar con `invalidateComandasCocina()`.
 * @param params Sucursal y áreas a consultar.
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 */
export const useComandasCocinaVersion = (
  params: ApiComandasCocinaVersionParams,
  options: QueryOpts<ComandaCocinaVersionProps[]> = {},
) =>
  useApiQuery(
    KEY_COMANDAS_COCINA_VERSION,
    (ctx) => apiComandasCocinaVersion(params, { signal: ctx.signal }),
    {
      ...options,
      enabled: options.enabled ?? true,
      queryKeys: [params.codigoSucursal, params.areas, ...(options.queryKeys ?? [])],
    },
  )
